import AppDialog from "components/common/AppDialog";
import CustomButton from "components/common/CustomButton";
import type { ScheduleItem } from "features/integrationsBeta/integrationsBeta";
import { useCreateScheduleMutation } from "features/integrationsBeta/schedules";
import { pushNotification } from "features/notificationSlice";
import { useAppDispatch } from "features/store";
import { useEffect } from "react";
import { usePendingChanges } from "./pending/PendingChangesContext";
import ScheduleFormFields, { useScheduleForm } from "./ScheduleFormFields";

interface Props {
  open: boolean;
  onClose: () => void;
  integrationId: number;
  schedule?: ScheduleItem;
}

/**
 * Create/edit dialog around the shared schedule builder form. Creating is
 * immediate; editing STAGES the changes - they are saved together from the
 * Settings save bar.
 */
export default function ScheduleBuilderDialog({
  open,
  onClose,
  integrationId,
  schedule,
}: Props) {
  const dispatch = useAppDispatch();
  const [createSchedule, { isLoading: creating }] = useCreateScheduleMutation();
  const { stage, getPending } = usePendingChanges();
  const form = useScheduleForm();
  const { reset } = form;

  // Re-seed the form on open, preferring already-staged values (reset is
  // recreated per render - deliberately not a dependency, or the effect
  // would clobber user input every render).
  useEffect(() => {
    if (!open) return;
    if (schedule) {
      const pendingName = getPending("schedule", schedule.id, "display_name");
      const pendingCron = getPending(
        "schedule",
        schedule.id,
        "cron_expression",
      );
      reset({
        displayName:
          (pendingName?.newValue as string | undefined) ??
          (schedule.display_name || schedule.name),
        cron:
          (pendingCron?.newValue as string | undefined) ??
          schedule.cron_expression,
      });
    } else {
      reset(undefined);
    }
  }, [open, schedule]);

  const canSave = form.isValid && !creating;

  const handleSave = async () => {
    if (schedule) {
      // oldValue matches the form's seed (display_name || name), so an
      // untouched form stages nothing.
      const entityLabel = `Schedule "${schedule.display_name || schedule.name}"`;
      const base = {
        entityType: "schedule" as const,
        entityId: schedule.id,
        section: "schedules" as const,
        entityLabel,
      };
      stage({
        ...base,
        field: "display_name",
        fieldLabel: "Name",
        valueKind: "text",
        oldValue: schedule.display_name || schedule.name,
        newValue: form.name.trim(),
      });
      stage({
        ...base,
        field: "cron_expression",
        fieldLabel: "Schedule",
        valueKind: "cron",
        oldValue: schedule.cron_expression,
        newValue: form.cron,
      });
      onClose();
      return;
    }
    try {
      await createSchedule({
        integration: integrationId,
        display_name: form.name.trim(),
        cron_expression: form.cron,
      }).unwrap();
      dispatch(
        pushNotification({ severity: "success", message: "Schedule saved." }),
      );
      onClose();
    } catch {
      dispatch(
        pushNotification({
          severity: "error",
          message: "Could not save the schedule.",
        }),
      );
    }
  };

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      title={schedule ? "Edit Schedule" : "New Schedule"}
      maxWidth="sm"
      showCloseIcon
      actions={
        <>
          <CustomButton fullWidth={false} onClick={onClose}>
            Cancel
          </CustomButton>
          <CustomButton
            fullWidth={false}
            onClick={handleSave}
            disabled={!canSave}
          >
            {schedule ? "Apply" : "Save"}
          </CustomButton>
        </>
      }
    >
      <ScheduleFormFields form={form} eventBridgeName={schedule?.name} />
    </AppDialog>
  );
}
